/**
 * Preview music — a game's published music loop as the soundtrack of its
 * `GameMediaStory` (the Explore feed slide and the game page hero): the
 * story owns playback, the surfaces only resolve the url and place the toggle.
 *
 * The previews themselves stay silent by construction: the reel is a
 * video-only canvas capture and the demo iframe runs `?demo=1`, which the
 * shell's audio gate mutes (ledger row 2.6). Sound is opt-in here,
 * Instagram-style:
 *  - ONE session-wide switch. Turned on, it stays on across swipes and pages
 *    until the viewer mutes it with the same button; a new tab starts muted.
 *  - ONE shared `<audio>` element, so two surfaces can never play over each
 *    other, and so the element the unmute tap unlocked (iOS requires a gesture
 *    per element) is the one every later game plays through.
 *  - The track is the one the active version PUBLISHED
 *    (`gates.mediaInventory.sfx.music`), resolved to its attached asset — the
 *    same bytes the game binds in play. Generated loops are trimmed to loop
 *    end-to-end, so the element's own `loop` is the whole loop contract.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import type { Creation, CreationVersion } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useDocumentVisible } from '../hooks/useDocumentVisible.js';

const SOUND_KEY = 'djgg.preview.sound';
/** Trailer music sits at half volume, like the in-game music bus (shell 0.42.0). */
const TRAILER_MUSIC_VOLUME = 0.5;

function readSound(): boolean {
  try {
    return sessionStorage.getItem(SOUND_KEY) === 'on';
  } catch {
    return false;
  }
}

let soundOn = readSound();
const listeners = new Set<() => void>();

function setSound(on: boolean): void {
  soundOn = on;
  try {
    sessionStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    /* storage unavailable: the switch still holds for this page's lifetime */
  }
  for (const listener of listeners) listener();
}

function subscribeSound(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let player: HTMLAudioElement | null = null;
function sharedPlayer(): HTMLAudioElement {
  if (!player) {
    player = new Audio();
    player.loop = true;
    player.volume = TRAILER_MUSIC_VOLUME;
    player.preload = 'auto';
  }
  return player;
}

function start(url: string): void {
  const el = sharedPlayer();
  if (el.src !== url) el.src = url;
  el.play().catch((error: unknown) => {
    // No gesture has unlocked playback yet (a reload restores "on" from the
    // session without one): show the truth — muted — rather than a lying icon.
    if (error instanceof DOMException && error.name === 'NotAllowedError') setSound(false);
  });
}

/** The session-wide preview sound switch. */
export function usePreviewSound(): boolean {
  return useSyncExternalStore(subscribeSound, () => soundOn);
}

/** The attached asset URL of the music a version published, or null when it binds none. */
export function publishedMusicUrl(creation: Pick<Creation, 'assets'>, version: Pick<CreationVersion, 'gates'> | null | undefined): string | null {
  const music = version?.gates?.mediaInventory?.sfx?.music;
  if (!music || music === 'none') return null;
  const asset = (creation.assets ?? []).find(
    (a) => a.kind === 'music' && a.variantId === music.variantId && a.contentSha256 === music.contentSha256,
  );
  return asset?.url ?? null;
}

// One read per active version for the page's lifetime: swiping back to a game
// must not refetch, and versions are immutable once published.
const activeVersions = new Map<string, Promise<CreationVersion | null>>();

/**
 * The published music of a creation's ACTIVE version, for surfaces that hold
 * the creation doc but not its version rows (the Explore feed). `undefined`
 * while resolving, `null` when the game has no music.
 */
export function useActiveMusicUrl(creation: (Creation & { id: string }) | undefined): string | null | undefined {
  const services = useServices();
  const [resolved, setResolved] = useState<{ key: string; url: string | null }>();
  const key = creation && creation.activeVersion > 0 ? `${creation.id}:${creation.activeVersion}` : null;
  useEffect(() => {
    if (!creation || !key) return;
    let current = true;
    let read = activeVersions.get(key);
    if (!read) {
      read = services.repos.versions.get(creation.id, creation.activeVersion);
      activeVersions.set(key, read);
      read.catch(() => activeVersions.delete(key));
    }
    read
      .then((version) => current && setResolved({ key, url: publishedMusicUrl(creation, version) }))
      .catch(() => current && setResolved({ key, url: null }));
    return () => {
      current = false;
    };
  }, [creation, key, services]);
  if (!key) return null;
  return resolved?.key === key ? resolved.url : undefined;
}

/**
 * Play `url` through the shared element while the switch is on, `active` holds
 * and the tab is showing; pause otherwise. Switching url starts the new game's
 * loop; returning to a paused one resumes it.
 */
export function usePreviewMusic(url: string | null | undefined, active: boolean): void {
  const on = usePreviewSound();
  const visible = useDocumentVisible();
  const playing = Boolean(url) && on && active && visible;
  useEffect(() => {
    if (!playing || !url) return;
    start(url);
    return () => sharedPlayer().pause();
  }, [playing, url]);
}

function SpeakerIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" />
      {on ? (
        <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />
      ) : (
        <path d="M17 9.5l5 5M22 9.5l-5 5" />
      )}
    </svg>
  );
}

/**
 * The mute/unmute button. The unmute tap starts `url` inside the gesture
 * itself — the unlock iOS needs — before the effect-driven playback takes over.
 */
export function PreviewSoundToggle({
  url,
  className,
  showLabel = false,
}: {
  url: string | null | undefined;
  className: string;
  showLabel?: boolean;
}) {
  const on = usePreviewSound();
  const toggle = () => {
    if (on) {
      setSound(false);
      return;
    }
    if (url) start(url);
    setSound(true);
  };
  const label = url === null ? 'No music' : on ? 'Sound' : 'Muted';
  return (
    <button
      type="button"
      className={className}
      onClick={toggle}
      aria-pressed={on}
      aria-label={on ? 'Mute game music' : 'Play game music'}
      title={url === null ? 'This game has no music' : undefined}
      data-testid="preview-sound-toggle"
    >
      <SpeakerIcon on={on} />
      {showLabel && <strong>{label}</strong>}
    </button>
  );
}
